/**
 * CompetitionComplete.jsx (iter226) — End screen shown once the judge has
 * finished all 3 objectives. Big brand statement + three pillars + a return
 * button.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Trophy, ArrowRight, ArrowClockwise, Lightning, ShieldCheck,
  MagnifyingGlass, Compass,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { trackCompetitionEvent, resetCompetitionDemo } from '../lib/api';

export default function CompetitionComplete() {
  const navigate = useNavigate();
  useEffect(() => {
    trackCompetitionEvent('demo.challenge_complete_viewed').catch(() => {});
  }, []);

  const restart = async () => {
    try {
      await resetCompetitionDemo();
      toast.success('Challenge reset.');
      navigate('/competition-demo/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="competition-complete">
      <div className="max-w-5xl mx-auto px-6 pt-20 pb-16">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.4em] text-emerald-400 mb-6">
          <Trophy size={16} weight="duotone" /> Challenge Complete
        </div>
        <h1 className="text-5xl md:text-7xl font-black leading-[1.02] tracking-tight">
          THIS IS<br />
          <span className="text-emerald-400">STOCKARIS</span>
        </h1>
        <p className="mt-6 text-xl text-neutral-300 max-w-2xl">
          A WMS built around the real warehouse.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-1">
          <div className="p-8 bg-neutral-900/60 border-l-4 border-emerald-500">
            <MagnifyingGlass size={28} weight="duotone" className="text-emerald-400 mb-4" />
            <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2">Inventory Intelligence</div>
            <p className="text-neutral-200">Don't just find the discrepancy. Find where to look.</p>
          </div>
          <div className="p-8 bg-neutral-900/60 border-l-4 border-sky-500">
            <Lightning size={28} weight="duotone" className="text-sky-400 mb-4" />
            <div className="text-xs font-mono uppercase tracking-widest text-sky-400 mb-2">Warehouse-Floor Control</div>
            <p className="text-neutral-200">Put the right tools in the hands of the people doing the work.</p>
          </div>
          <div className="p-8 bg-neutral-900/60 border-l-4 border-amber-500">
            <ShieldCheck size={28} weight="duotone" className="text-amber-400 mb-4" />
            <div className="text-xs font-mono uppercase tracking-widest text-amber-400 mb-2">Proactive Planning</div>
            <p className="text-neutral-200">Identify capacity, inventory and operational problems before they become expensive problems.</p>
          </div>
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          <Button size="lg"
            onClick={() => navigate('/competition-demo/dashboard')}
            className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-black text-sm tracking-widest uppercase px-8 py-6"
            data-testid="cc-return-btn">
            <Compass size={20} className="mr-2" />
            Return to Stockaris
            <ArrowRight size={16} className="ml-2" />
          </Button>
          <Button size="lg" variant="outline"
            onClick={restart}
            className="rounded-none border-neutral-700 hover:border-emerald-500 hover:text-emerald-400 bg-transparent text-neutral-100 font-black text-sm tracking-widest uppercase px-8 py-6"
            data-testid="cc-restart-btn">
            <ArrowClockwise size={20} className="mr-2" />
            Restart the Challenge
          </Button>
        </div>
      </div>
    </div>
  );
}
